import { useState, useEffect } from 'react';
import { api } from '../../../services/api';

interface ChampMesure {
  id: string;
  nom: string;
  unite: string;
  description?: string;
  obligatoire: boolean;
}

interface MesuresDynamiques {
  [champId: string]: string;
  notes: string;
}

interface FormulaireMesuresProps {
  mesures: MesuresDynamiques;
  onChange: (field: string, value: string) => void;
  clientId?: string;
}

export default function FormulaireMesures({ mesures, onChange, clientId }: FormulaireMesuresProps) {
  const [champs, setChamps] = useState<ChampMesure[]>([]);
  const [loading, setLoading] = useState(true);
  const [profilLoaded, setProfilLoaded] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('atelier_bassoum_token');
    if (token) {
        api.getMesureChamps(token).then(data => {
            setChamps(data.map((c: any) => ({
                id: String(c.id),
                nom: c.nom,
                unite: c.unite || 'cm',
                description: c.description,
                obligatoire: !!c.obligatoire
            })));
            setLoading(false);
        }).catch(() => setLoading(false));
    }
  }, []);

  // Auto-charger les mesures du client
  useEffect(() => {
    if (!clientId || loading) return;
    const token = localStorage.getItem('atelier_bassoum_token');
    if (token && !profilLoaded) {
        api.getClientMesures(token, Number(clientId)).then(data => {
            data.forEach((m: any) => {
                if (m.valeur) onChange(String(m.champ_mesure_id), m.valeur);
            });
            setProfilLoaded(true);
        }).catch(() => {});
    }
  }, [clientId, loading, profilLoaded]);

  if (loading) return <div className="text-center py-10 animate-pulse text-gray-400 text-sm">Chargement des paramètres de mesure...</div>;

  const champsMandatoires = champs.filter(c => c.obligatoire);
  const champsOptionnels = champs.filter(c => !c.obligatoire);

  return (
    <div className="space-y-6">
      <div className="bg-or-50 border border-or-200 rounded-xl px-4 py-3 flex items-start gap-3">
        <i className="ri-information-line text-or-600 mt-0.5" />
        <p className="text-xs text-or-700">
          Saisissez les mesures en <strong>centimètres (cm)</strong>. Les champs marqués <span className="text-red-500">*</span> sont requis pour la confection.
        </p>
      </div>

      {champsMandatoires.length > 0 && (
        <section>
          <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3 flex items-center gap-2">
            <div className="w-1 h-3 bg-foret-500 rounded-full" />
            Mesures obligatoires
          </h4>
          <div className="grid grid-cols-2 gap-4">
            {champsMandatoires.map(champ => (
              <div key={champ.id}>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                  {champ.nom} <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                   <input
                    type="number"
                    value={mesures[champ.id] || ''}
                    onChange={e => onChange(champ.id, e.target.value)}
                    placeholder="0.0"
                    step="0.1"
                    className="w-full pl-4 pr-10 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-foret-400 bg-gray-50 font-bold"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-bold text-gray-400 uppercase">{champ.unite}</span>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {champsOptionnels.length > 0 && (
          <section>
            <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3 flex items-center gap-2">
              <div className="w-1 h-3 bg-gray-300 rounded-full" />
              Mesures complémentaires
            </h4>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
              {champsOptionnels.map(champ => (
                <div key={champ.id}>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">{champ.nom}</label>
                  <div className="relative">
                    <input
                      type="number"
                      value={mesures[champ.id] || ''}
                      onChange={e => onChange(champ.id, e.target.value)}
                      placeholder="—"
                      step="0.1"
                      className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-300 bg-gray-50"
                    />
                     <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-gray-400">{champ.unite}</span>
                  </div>
                </div>
              ))}
            </div>
          </section>
      )}

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-1.5 flex items-center gap-2">
          Notes & instructions spéciales
          <span className="text-[10px] text-gray-400 font-normal">(optionnel)</span>
        </label>
        <textarea
          value={mesures.notes || ''}
          onChange={e => onChange('notes', e.target.value)}
          rows={3}
          placeholder="ex: Encolure arrondie, manches 3/4..."
          className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-foret-400 bg-gray-50 resize-none"
        />
      </div>
    </div>
  );
}
